<template>
  <div v-if="show" class="fixed inset-0 w-full h-full bg-black/[0.04] flex items-center justify-center z-[1000] backdrop-blur-sm" @click.self="handleBackdropClick">
    <div class="relative overflow-auto bg-n-alpha-3 rtl:text-right skip-context-menu rounded-xl w-[500px] max-w-[90%] max-h-[90vh] overflow-y-auto shadow-xl">
      <!-- Header -->
      <div class="flex justify-between items-center px-6 py-4 border-b border-n-slate-3 dark:border-n-slate-6">
        <h3 class="text-lg font-semibold m-0">{{ title }}</h3>
        <button
          v-if="showCloseButton && type !== 'warn'"
          class="z-10 ltr:right-2 rtl:left-2 top-2 inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-10 w-10 p-0 text-sm font-medium justify-center"
          @click="handleClose"
        >
          <span class="i-lucide-x flex-shrink-0"></span>
        </button>
      </div>

      <!-- Body -->
      <div class="p-6">
        <div class="flex gap-4 items-start">
          <!-- Icon based on type -->
          <div v-if="showIcon" class="flex-shrink-0 mt-0.5">
            <div v-if="type === 'success'" class="flex items-center justify-center w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/20">
              <span class="i-lucide-check flex-shrink-0 text-green-600 dark:text-green-400 w-5 h-5"></span>
            </div>
            <div v-else-if="type === 'warn' || type === 'warning-confirmation'" class="flex items-center justify-center w-10 h-10 rounded-full bg-yellow-100 dark:bg-amber-900/20">
              <span class="i-lucide-alert-triangle flex-shrink-0 text-amber-600 dark:text-amber-600 w-5 h-5"></span>
            </div>
            <div v-else-if="type === 'error'" class="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/20">
              <span class="i-lucide-x-circle flex-shrink-0 text-red-600 dark:text-red-400 w-5 h-5"></span>
            </div>
            <div v-else-if="type === 'confirmation'" class="flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-700">
              <span class="i-lucide-help-circle flex-shrink-0 text-slate-600 dark:text-slate-300 w-5 h-5"></span>
            </div>
            <div v-else-if="type === 'delete'" class="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/20">
              <span class="i-lucide-trash-2 flex-shrink-0 text-red-600 dark:text-red-400 w-5 h-5"></span>
            </div>
            <div v-else-if="type === 'custom'" class="flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-700">
              <span class="i-lucide-info flex-shrink-0 text-slate-600 dark:text-slate-300 w-5 h-5"></span>
            </div>
          </div>

          <!-- Message content -->
          <div class="flex-1">
            <p class="text-sm text-n-slate-12 m-0 leading-6 break-words">{{ message }}</p>
          </div>
        </div>
        <p v-if="error" class="text-red-500 text-sm mt-1">{{ error }}</p>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-3 px-6 py-4 border-t border-n-slate-3 dark:border-n-slate-6">
        <!-- Success type: Only FECHAR button -->
        <template v-if="type === 'success'">
          <button
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="handleClose"
          >
            {{ successButtonText || 'FECHAR' }}
          </button>
        </template>

        <!-- Warn type: Only OK button (yellow/amber) -->
        <template v-else-if="type === 'warn'">
          <button
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-yellow-500 text-white hover:enabled:bg-yellow-600 focus-visible:bg-yellow-600 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="handleClose"
          >
            {{ confirmText || 'OK' }}
          </button>
        </template>

        <!-- Warning Confirmation type: Cancel and Confirm buttons (yellow theme) -->
        <template v-else-if="type === 'warning-confirmation'">
          <button
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="handleCancel"
          >
            {{ cancelText || 'Cancelar' }}
          </button>
          <button
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-yellow-500 text-white hover:enabled:bg-yellow-600 focus-visible:bg-yellow-600 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="handleConfirm"
          >
            <span v-if="loading" class="animate-spin i-lucide-loader"></span>
            <span v-else>{{ confirmText || 'Confirmar' }}</span>
          </button>
        </template>

        <!-- Error type: Custom button or default -->
        <template v-else-if="type === 'error'">
          <button
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-ruby-9 text-white hover:enabled:bg-n-ruby-10 focus-visible:bg-n-ruby-10 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="handleErrorAction"
          >
            {{ errorButtonText || 'OK' }}
          </button>
        </template>

        <!-- Delete type: Cancel and Delete buttons -->
        <template v-else-if="type === 'delete'">
          <button
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="handleCancel"
          >
            {{ cancelText || 'Cancelar' }}
          </button>
          <button
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-ruby-9 text-white hover:enabled:bg-n-ruby-10 focus-visible:bg-n-ruby-10 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="handleConfirm"
          >
            <span v-if="loading" class="animate-spin i-lucide-loader"></span>
            <span v-else>{{ confirmText || 'Excluir' }}</span>
          </button>
        </template>

        <!-- Confirmation type: Cancel and Confirm buttons -->
        <template v-else-if="type === 'confirmation'">
          <button
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="handleCancel"
          >
            {{ cancelText || 'Cancelar' }}
          </button>
          <button
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="handleConfirm"
          >
            <span v-if="loading" class="animate-spin i-lucide-loader"></span>
            <span v-else>{{ confirmText || 'Confirmar' }}</span>
          </button>
        </template>

        <!-- Custom type: Custom buttons -->
        <template v-else-if="type === 'custom'">
          <button
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="handleCancel"
          >
            {{ cancelText || 'Cancelar' }}
          </button>
          <button
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="handleConfirm"
          >
            <span v-if="loading" class="animate-spin i-lucide-loader"></span>
            <span v-else>{{ confirmText || 'Confirmar' }}</span>
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AlertResponza',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'success',
      validator: value => ['success', 'warn', 'warning-confirmation', 'error', 'confirmation', 'delete', 'custom'].includes(value),
    },
    title: {
      type: String,
      default: '',
    },
    message: {
      type: String,
      required: true,
    },
    showIcon: {
      type: Boolean,
      default: true,
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    errorButtonText: {
      type: String,
      default: '',
    },
    successButtonText: {
      type: String,
      default: '',
    },
    onErrorAction: {
      type: Function,
      default: null,
    },
    confirmText: {
      type: String,
      default: '',
    },
    cancelText: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
  },
  emits: ['close', 'confirm', 'cancel'],
  setup(props, { emit }) {
    const handleClose = () => {
      emit('close');
    };

    const handleConfirm = () => {
      emit('confirm');
    };

    const handleCancel = () => {
      emit('cancel');
    };

    const handleErrorAction = () => {
      if (props.onErrorAction && typeof props.onErrorAction === 'function') {
        props.onErrorAction();
      }
      emit('close');
    };

    const handleBackdropClick = () => {
      // Only close on backdrop click for non-confirmation, non-delete, non-custom, non-warn and non-warning-confirmation types
      if (props.type !== 'confirmation' && props.type !== 'delete' && props.type !== 'custom' && props.type !== 'warn' && props.type !== 'warning-confirmation') {
        handleClose();
      }
    };

    return {
      handleClose,
      handleConfirm,
      handleCancel,
      handleErrorAction,
      handleBackdropClick,
    };
  },
};
</script>
